import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import usePersistentState from '../utils/usePersistentState';
import toast from 'react-hot-toast';
import { taskAPI, teamAPI, authAPI, requirementAPI, candidateAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button, Input, Select, Textarea, Modal, Badge, EmptyState, Spinner, SearchableSelect, ConfirmModal} from '../components/UI';
import RichTextEditor from '../components/RichTextEditor';
import SafeHtml from '../components/SafeHtml';
import HistoryPanel from '../components/HistoryPanel';
import GlobalHistoryPanel from '../components/GlobalHistoryPanel';

const PRIORITY_COLOR = { High: 'danger', Medium: 'warning', Low: 'info' };
const STATUS_COLOR   = { 'To Do': 'default', 'In Progress': 'warning', 'Completed': 'success' };
const STATUSES       = ['To Do', 'In Progress', 'Completed'];
const PRIORITIES     = ['Low', 'Medium', 'High'];

const emptyForm = {
  title: '', description: '', requirementId: '', candidateId: '',
  assignedToUserId: '', assignedToTeamId: '', priority: 'Medium', deadline: '',
};

function TaskCard({ task, onEdit, onDelete, onStatusChange, canEdit, onOpen, onHistory, deleting }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [hovered, setHovered] = useState(false);
  const isOverdue = task.deadline && new Date(task.deadline) < new Date() && task.status !== 'Completed';
  return (
    <div
      style={{
        background: hovered ? 'var(--bg-hover)' : 'var(--bg-card)',
        border: `1px solid ${hovered ? 'var(--border-brand)' : 'var(--border-subtle)'}`,
        borderRadius: 12,
        padding: 16,
        cursor: 'pointer',
        transition: 'all 0.15s',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onOpen(task)}>
      <div className="flex items-start justify-between mb-2">
        <Badge variant={PRIORITY_COLOR[task.priority]} size="sm">{task.priority}</Badge>
        <Badge variant={STATUS_COLOR[task.status]} size="sm">{task.status}</Badge>
      </div>
      <p style={{ color: 'var(--text-primary)', fontWeight: 500, fontSize: 13, marginBottom: 4 }} className="line-clamp-2">{task.title}</p>
      {task.description && <SafeHtml as="div" html={task.description} className="line-clamp-2" style={{ color: 'var(--text-secondary)', fontSize: 12, marginBottom: 8 }} />}

      <div className="space-y-1 min-w-0" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
        {task.assignedToUser && <p className="truncate">👤 {task.assignedToUser.name}</p>}
        {task.assignedToTeam && <p className="truncate">👥 {task.assignedToTeam.name}</p>}
        {task.requirement    && <p className="truncate">📋 {task.requirement.jobTitle}</p>}
        {task.candidate      && <p className="truncate">🧑 {task.candidate.fullName}</p>}
        {task.deadline && (
          <p style={isOverdue ? { color: '#f87171', fontWeight: 500 } : {}}>
            🗓 {isOverdue ? '⚠ Overdue · ' : ''}{new Date(task.deadline).toLocaleDateString()}
          </p>
        )}
        {task.comments?.length > 0 && <p>💬 {task.comments.length} comment{task.comments.length !== 1 ? 's' : ''}</p>}
      </div>

      <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 8, marginTop: 8 }}
        className="flex gap-2 items-center" onClick={e => e.stopPropagation()}>
        <Button variant="ghost" size="xs" iconOnly tooltip="View history" onClick={() => onHistory(task)}>🕐</Button>
        {canEdit && (
          <>
            <Button variant="outline" size="xs" onClick={() => onEdit(task)}>Edit</Button>
            <Button variant="danger" size="xs" style={{ marginLeft: 'auto' }} loading={deleting === task.id} loadingText="Deleting…" onClick={() => onDelete(task.id)}>Delete</Button>
          </>
        )}
      </div>
    </div>
  );
}

function KanbanColumn({ status, tasks, onEdit, onDelete, onStatusChange, canEdit, onOpen, onHistory, deleting }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const DOT_COLOR = { 'To Do': '#60a5fa', 'In Progress': '#fbbf24', 'Completed': '#34d399' };
  return (
    <div className="flex-1 min-w-64 md:min-w-72">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, paddingLeft: 4 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: DOT_COLOR[status] }} />
        <h3 style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>{status}</h3>
        <span style={{ color: 'var(--text-muted)', fontSize: 12, marginLeft: 'auto' }}>{tasks.length}</span>
      </div>
      <div className="space-y-3">
        {tasks.map(t => (
          <TaskCard key={t.id} task={t} onEdit={onEdit} onDelete={onDelete}
            onStatusChange={onStatusChange} canEdit={canEdit} onOpen={onOpen} onHistory={onHistory} deleting={deleting} />
        ))}
        {tasks.length === 0 && (
          <div style={{
            border: `2px dashed ${isDark ? '#1e293b' : '#e2e8f0'}`,
            borderRadius: 12,
            padding: 24,
            textAlign: 'center',
            color: 'var(--text-muted)',
            fontSize: 12,
          }}>
            No tasks
          </div>
        )}
      </div>
    </div>
  );
}

export default function Tasks() {
  const { hasRole, user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const canCreate = hasRole(['SuperAdmin', 'RecruiterManager']);
  const canEdit   = hasRole(['SuperAdmin', 'RecruiterManager']);

  const [tasks, setTasks]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [view, setView]           = useState('kanban'); // 'kanban' | 'list'
  // Kept for this tab — survives leaving the page and coming back.
  const [filters, setFilters]     = usePersistentState('tasks:filters', { status: '', priority: '', teamId: '', search: '' });
  const [modalOpen, setModalOpen] = useState(false);
  const [detailTask, setDetailTask] = useState(null);
  const [editTask, setEditTask]   = useState(null);
  const [form, setForm]           = useState(emptyForm);
  const [saving, setSaving]       = useState(false);
  const [deleting, setDeleting]   = useState(null);
  const [comment, setComment]     = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  const [teams, setTeams]             = useState([]);
  const [users, setUsers]             = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [candidates, setCandidates]   = useState([]);
  const [historyPanel, setHistoryPanel] = useState({ open: false, id: null, name: '' });
  const [globalHistory, setGlobalHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filters.status)   params.status   = filters.status;
      if (filters.priority) params.priority = filters.priority;
      if (filters.teamId)   params.teamId   = filters.teamId;
      if (filters.search)   params.search   = filters.search;
      const { data } = await taskAPI.getAll(params);
      setTasks(data.tasks);
    } catch { toast.error('Failed to load tasks'); }
    finally { setLoading(false); }
  }, [filters]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    teamAPI.getAll({ limit: 100 }).then(({ data }) => setTeams(data.teams)).catch(() => {});
    requirementAPI.dropdown().then(({ data }) => setRequirements(data.requirements || [])).catch(() => {});
    candidateAPI.dropdown().then(({ data }) => setCandidates(data.candidates || [])).catch(() => {});
  }, []);

  // Assignee options — only the roles that can create/edit tasks need them, and
  // /auth/users/assignable (unlike the SuperAdmin-only /auth/users) is open to
  // RecruiterManagers too. A failure is surfaced: an empty list here silently
  // blocks assigning a task to a person.
  useEffect(() => {
    if (!canEdit) return;
    authAPI.getAssignableUsers()
      .then(({ data }) => setUsers(data.users || []))
      .catch((err) => toast.error(err.response?.data?.message || 'Could not load users to assign tasks to'));
  }, [canEdit]);

  const openCreate = () => { setEditTask(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit = (t) => {
    setEditTask(t);
    setForm({
      title: t.title, description: t.description || '',
      requirementId: t.requirementId || '', candidateId: t.candidateId || '',
      assignedToUserId: t.assignedToUserId || '', assignedToTeamId: t.assignedToTeamId || '',
      priority: t.priority, deadline: t.deadline ? t.deadline.split('T')[0] : '',
    });
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      requirementId:    form.requirementId    || null,
      candidateId:      form.candidateId      || null,
      assignedToUserId: form.assignedToUserId || null,
      assignedToTeamId: form.assignedToTeamId || null,
      deadline:         form.deadline         || null,
    };
    try {
      if (editTask) { await taskAPI.update(editTask.id, payload); toast.success('Task updated!'); }
      else          { await taskAPI.create(payload);              toast.success('Task created!'); }
      setModalOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = (id) => { setConfirmDelete(id); };

  const handleDeleteConfirmed = async () => {
    const id = confirmDelete;
    setConfirmDelete(null);
    setDeleting(id);
    try { await taskAPI.delete(id); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
    finally { setDeleting(null); }
  };

  const handleStatusChange = async (taskId, status) => {
    try { await taskAPI.updateStatus(taskId, status); toast.success(`Moved to "${status}"`); load(); }
    catch { toast.error('Update failed'); }
  };

  const openDetail = async (task) => {
    const { data } = await taskAPI.getById(task.id);
    setDetailTask(data.task);
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;
    setSubmittingComment(true);
    try {
      await taskAPI.addComment(detailTask.id, comment);
      const { data } = await taskAPI.getById(detailTask.id);
      setDetailTask(data.task);
      setComment('');
      toast.success('Comment added');
    } catch { toast.error('Failed to add comment'); }
    finally { setSubmittingComment(false); }
  };

  const byStatus = (status) => tasks.filter(t => t.status === status);

  // ── Scroll-lock: prevent body scroll when any overlay is open ────────────
  const anyOverlayOpen = modalOpen || !!detailTask || historyPanel.open || globalHistory || !!confirmDelete;
  useLayoutEffect(() => {
    if (anyOverlayOpen) {
      document.documentElement.classList.add('scroll-locked');
      return () => { document.documentElement.classList.remove('scroll-locked'); };
    }
  }, [anyOverlayOpen]);
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>Tasks</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginTop: 4 }}>{tasks.length} tasks</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div style={{ display: 'flex', background: 'var(--bg-raised)', borderRadius: 8, padding: 4, gap: 2 }}>
            {['kanban', 'list'].map(v => (
              <Button key={v} size="sm" variant={view === v ? 'primary' : 'ghost'} onClick={() => setView(v)}>
                {v === 'kanban' ? '⬛ Kanban' : '☰ List'}
              </Button>
            ))}
          </div>
          <Button variant="secondary" onClick={() => setGlobalHistory(true)}>🕐 History</Button>
          {canCreate && <Button onClick={openCreate}>+ New Task</Button>}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Input placeholder="Search tasks…" value={filters.search}
          onChange={e => setFilters({ ...filters, search: e.target.value })} className="w-full sm:w-auto sm:max-w-xs" />
        <Select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} className="w-full sm:w-36">
          <option value="">All Status</option>
          {STATUSES.map(s => <option key={s}>{s}</option>)}
        </Select>
        <Select value={filters.priority} onChange={e => setFilters({ ...filters, priority: e.target.value })} className="w-full sm:w-32">
          <option value="">All Priority</option>
          {PRIORITIES.map(p => <option key={p}>{p}</option>)}
        </Select>
        <Select value={filters.teamId} onChange={e => setFilters({ ...filters, teamId: e.target.value })} className="w-full sm:w-40">
          <option value="">All Teams</option>
          {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Select>
      </div>

      {loading ? <div className="py-16 flex justify-center"><Spinner /></div> : tasks.length === 0 ? (
        <EmptyState icon="📋" title="No tasks found" message="Create your first task."
          action={canCreate && <Button onClick={openCreate}>New Task</Button>} />
      ) : view === 'kanban' ? (
        <div className="flex gap-5 overflow-x-auto pb-4">
          {STATUSES.map(status => (
            <KanbanColumn key={status} status={status} tasks={byStatus(status)}
              onEdit={openEdit} onDelete={handleDelete} onStatusChange={handleStatusChange}
              canEdit={canEdit} onOpen={openDetail} deleting={deleting}
              onHistory={t => setHistoryPanel({ open: true, id: t.id, name: t.title })} />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {tasks.map(task => {
            const isOverdue = task.deadline && new Date(task.deadline) < new Date() && task.status !== 'Completed';
            return (
              <div key={task.id}
                style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '12px 20px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, cursor: 'pointer', transition: 'border-color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-brand)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
                onClick={() => openDetail(task)}>
                <div className="flex-1 min-w-0">
                  <p style={{ color: 'var(--text-primary)', fontWeight: 500, fontSize: 13 }} className="truncate">{task.title}</p>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 2 }} className="truncate">
                    {task.assignedToUser?.name || task.assignedToTeam?.name || 'Unassigned'}
                    {task.requirement && ` · ${task.requirement.jobTitle}`}
                  </p>
                </div>
                <Badge variant={PRIORITY_COLOR[task.priority]} size="sm">{task.priority}</Badge>
                <Badge variant={STATUS_COLOR[task.status]} size="sm">{task.status}</Badge>
                {task.deadline && (
                  <span style={{ fontSize: 12, color: isOverdue ? '#f87171' : 'var(--text-muted)' }} className="hidden md:block">
                    {isOverdue ? '⚠ ' : ''}{new Date(task.deadline).toLocaleDateString()}
                  </span>
                )}
                <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                  <Button variant="ghost" size="xs" iconOnly tooltip="View history"
                    onClick={() => setHistoryPanel({ open: true, id: task.id, name: task.title })}>🕐</Button>
                  {canEdit && (
                    <>
                      <Button variant="outline" size="xs" onClick={() => openEdit(task)}>Edit</Button>
                      <Button variant="danger" size="xs" loading={deleting === task.id} loadingText="Deleting…" onClick={() => handleDelete(task.id)}>Delete</Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}
        title={editTask ? 'Edit Task' : 'New Task'} size="lg">
        <form onSubmit={handleSave} className="space-y-4">
          <Input label="Title *" value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })} required />
          <RichTextEditor label="Description" value={form.description} rows={4}
            onChange={(html) => setForm({ ...form, description: html })} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select label="Assign to User" value={form.assignedToUserId}
              onChange={e => setForm({ ...form, assignedToUserId: e.target.value })}>
              <option value="">— None —</option>
              {users.filter(u => u.role !== 'SuperAdmin').map(u => (
                <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
              ))}
            </Select>
            <Select label="Assign to Team" value={form.assignedToTeamId}
              onChange={e => setForm({ ...form, assignedToTeamId: e.target.value })}>
              <option value="">— None —</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </Select>
            <Select label="Priority" value={form.priority}
              onChange={e => setForm({ ...form, priority: e.target.value })}>
              {PRIORITIES.map(p => <option key={p}>{p}</option>)}
            </Select>
            <Input label="Deadline" type="date" value={form.deadline}
              onChange={e => setForm({ ...form, deadline: e.target.value })} />
            <SearchableSelect
              label="Linked Requirement (Job)"
              value={form.requirementId}
              onChange={v => setForm({ ...form, requirementId: v })}
              placeholder="— None —"
              options={requirements.map(r => ({ value: r.id, label: r.jobTitle, sub: r.client?.clientName || '' }))}
            />
            <SearchableSelect
              label="Linked Candidate"
              value={form.candidateId}
              onChange={v => setForm({ ...form, candidateId: v })}
              placeholder="— None —"
              options={candidates.map(c => ({ value: c.id, label: c.fullName, sub: c.email || '' }))}
            />
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={saving}>{editTask ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </Modal>

      {/* Task Detail Modal */}
      <Modal isOpen={!!detailTask} onClose={() => setDetailTask(null)}
        title="Task Detail" size="lg">
        {detailTask && (
          <div className="space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h2 style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 18 }}>{detailTask.title}</h2>
                {detailTask.description && <SafeHtml as="div" html={detailTask.description} style={{ color: 'var(--text-secondary)', fontSize: 14, marginTop: 4 }} />}
              </div>
              <div className="flex gap-2 flex-col items-end">
                <Badge variant={PRIORITY_COLOR[detailTask.priority]}>{detailTask.priority}</Badge>
                <Badge variant={STATUS_COLOR[detailTask.status]}>{detailTask.status}</Badge>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" style={{ background: 'var(--bg-raised)', borderRadius: 8, padding: 16, fontSize: 14 }}>
              {detailTask.assignedToUser && <div><span style={{ color: 'var(--text-muted)' }}>Assignee: </span><span style={{ color: 'var(--text-primary)' }}>{detailTask.assignedToUser.name}</span></div>}
              {detailTask.assignedToTeam && <div><span style={{ color: 'var(--text-muted)' }}>Team: </span><span style={{ color: 'var(--text-primary)' }}>{detailTask.assignedToTeam.name}</span></div>}
              {detailTask.requirement    && <div><span style={{ color: 'var(--text-muted)' }}>Job: </span><span style={{ color: 'var(--text-primary)' }}>{detailTask.requirement.jobTitle}</span></div>}
              {detailTask.candidate      && <div><span style={{ color: 'var(--text-muted)' }}>Candidate: </span><span style={{ color: 'var(--text-primary)' }}>{detailTask.candidate.fullName}</span></div>}
              {detailTask.deadline       && <div><span style={{ color: 'var(--text-muted)' }}>Deadline: </span><span style={{ color: 'var(--text-primary)' }}>{new Date(detailTask.deadline).toLocaleDateString()}</span></div>}
              <div><span style={{ color: 'var(--text-muted)' }}>Created by: </span><span style={{ color: 'var(--text-primary)' }}>{detailTask.createdByUser?.name}</span></div>
            </div>

            {/* Status update for all roles */}
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Update Status</label>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map(s => (
                  <Button key={s} size="sm"
                    variant={detailTask.status === s ? 'primary' : 'ghost'}
                    style={{ flex: 1 }}
                    onClick={() => { handleStatusChange(detailTask.id, s); setDetailTask(prev => ({ ...prev, status: s })); }}>
                    {s}
                  </Button>
                ))}
              </div>
            </div>

            {/* Comments */}
            <div>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Comments ({detailTask.comments?.length || 0})</p>
              <div className="space-y-2 max-h-40 overflow-y-auto mb-3">
                {(detailTask.comments || []).map(c => (
                  <div key={c.id} style={{ background: 'var(--bg-raised)', borderRadius: 8, padding: 12 }}>
                    <div className="flex flex-wrap items-center gap-2 mb-1 min-w-0">
                      <span className="truncate min-w-0" style={{ color: '#818cf8', fontSize: 12, fontWeight: 500 }}>{c.user?.name}</span>
                      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{new Date(c.createdAt).toLocaleString()}</span>
                    </div>
                    <p className="break-words" style={{ color: 'var(--text-secondary)', fontSize: 13, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{c.comment}</p>
                  </div>
                ))}
                {(!detailTask.comments || detailTask.comments.length === 0) && (
                  <p style={{ color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', padding: '8px 0' }}>No comments yet</p>
                )}
              </div>
              <div className="flex gap-2">
                <input value={comment} onChange={e => setComment(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleAddComment()}
                  placeholder="Add a comment… (Enter to submit)"
                  style={{ flex: 1, minWidth: 0, background: 'var(--bg-input)', border: '1px solid var(--border-input)', color: 'var(--text-primary)', borderRadius: 8, padding: '8px 12px', fontSize: 14, outline: 'none' }} />
                <Button onClick={handleAddComment} loading={submittingComment}>Send</Button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Per-entity History Panel ── */}
      {historyPanel.open && (
        <HistoryPanel
          entityId={historyPanel.id}
          entityName={historyPanel.name}
          onClose={() => setHistoryPanel({ open: false, id: null, name: '' })}
        />
      )}

      {/* ── Global History Feed ── */}
      {globalHistory && (
        <GlobalHistoryPanel
          module="Task"
          onClose={() => setGlobalHistory(false)}
        />
      )}
            {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }} onClick={() => setConfirmDelete(null)} />
          <div style={{ position: 'relative', background: isDark ? '#1e2535' : '#ffffff', borderRadius: 16, padding: '28px', width: '100%', maxWidth: 400, boxShadow: '0 24px 64px rgba(0,0,0,0.4)' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700, color: isDark ? '#f1f5f9' : '#111827' }}>Delete Task?</h3>
            <p style={{ margin: '0 0 24px', fontSize: 14, color: isDark ? '#94a3b8' : '#6b7280' }}>This will permanently remove the task.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmDelete(null)} style={{ padding: '9px 20px', borderRadius: 8, border: '1.5px solid #d1d5db', background: 'transparent', color: isDark ? '#f1f5f9' : '#374151', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleDeleteConfirmed} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
